/**
 * The posts behind one chart bucket / calendar square (§5.1 #26).
 *
 * Clicking a mark in Metrics asks "which posts is this?", and the answer is the feed the app
 * already has: the same `#26` endpoint, narrowed to the bucket's `from`/`to` and to the post types
 * the chart counted, rendered with the same `FeedCard`. No server change and no new query shape —
 * the endpoint already takes both bounds and a `postTypes` list.
 *
 * The type list is passed in pre-intersected by the caller so the card count always matches the
 * number the tooltip showed. The title is the server's own bucket label, rendered verbatim (§12.5).
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import {
  MAX_FEED_LIMIT,
  type FeedQuery,
  type PostType,
  type SubjectType,
  type ViewFilterParams,
} from '@lw/shared';
import { getViewFeed } from '../../../api/views';
import { QK } from '../../../api/queryKeys';
import { EmptyState, ErrorState, Modal, Spinner } from '../../../components';
import { useInView } from '../../../lib/hooks';
import { FeedCard } from '../feed/FeedCard';
import { usePostPreview } from '../PreviewContext';

export type BucketPostsDialogProps = {
  subjectType: SubjectType;
  id: number;
  /** The view's active display filters — the popup narrows them, it never widens them. */
  baseParams: Partial<ViewFilterParams>;
  /** Server-built bucket label ("6 – 12 Aug 2026"), rendered verbatim as the title. */
  title: string;
  from: number;
  to: number;
  /** Exactly the types the chart counted (counted types ∩ any active filter). */
  postTypes: PostType[];
  mediaEnabled: boolean;
  onClose: () => void;
};

export function BucketPostsDialog({
  subjectType,
  id,
  baseParams,
  title,
  from,
  to,
  postTypes,
  mediaEnabled,
  onClose,
}: BucketPostsDialogProps) {
  const { openPreview } = usePostPreview();

  const baseQuery: Partial<FeedQuery> = {
    ...baseParams,
    from,
    to,
    postTypes,
    sort: 'publishedAt',
    dir: 'desc',
    limit: MAX_FEED_LIMIT,
  };

  const feed = useInfiniteQuery({
    queryKey: QK.viewFeed(subjectType, id, baseQuery),
    queryFn: ({ pageParam, signal }) =>
      getViewFeed(subjectType, id, { ...baseQuery, cursor: pageParam ?? undefined }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const cards = feed.data?.pages.flatMap((page) => page.cards) ?? [];
  const ids = cards.map((card) => card.id);

  const sentinelRef = useInView<HTMLDivElement>(
    feed.hasNextPage && !feed.isFetchingNextPage,
    () => void feed.fetchNextPage(),
    { rootMargin: '400px' },
  );

  return (
    <Modal open title={title} onClose={onClose} size="md">
      <div className="feed">
        {feed.isPending ? <Spinner size="lg" label="Loading posts…" /> : null}

        {feed.isError ? (
          <ErrorState
            title="Couldn't load these posts"
            error={feed.error}
            onRetry={() => void feed.refetch()}
            retrying={feed.isFetching}
          />
        ) : null}

        {/* Defensive: the mark is only clickable when the server counted posts here. */}
        {feed.isSuccess && cards.length === 0 ? (
          <EmptyState
            title="No posts here"
            description="Nothing matches this bucket with the current filters."
          />
        ) : null}

        <div className="feed__list">
          {cards.map((card) => (
            <FeedCard
              key={card.id}
              card={card}
              mediaEnabled={mediaEnabled}
              onOpen={(postId) => openPreview(postId, ids)}
            />
          ))}
        </div>

        <div ref={sentinelRef} className="feed__sentinel">
          {feed.isFetchingNextPage ? <Spinner label="Loading more…" /> : null}
        </div>
      </div>
    </Modal>
  );
}

export default BucketPostsDialog;
